import { useEffect, useState, type JSX } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid2'
import InputLabel from '@mui/material/InputLabel'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import Radio from '@mui/material/Radio'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import {
  DEFAULT_CREDIT_CAP_TOTAL,
  PRESETS,
  PROVIDER_LABEL,
  type JobSettings,
  type MeResponse,
  type ProviderId,
  type ProviderStatus,
} from '@profiler/shared'
import { PROVIDER_SWITCH_CONFLICT, isConflict, type AppSettings, type AppSettingsPatch } from '../api'
import { EmptyState, ErrorPanel, LoadingPanel } from '../components/States'
import { ZohoCrmSection } from '../components/ZohoCrmSection'
import { EM_DASH, formatDate, formatDateTime, formatNumber, percent } from '../format'
import { PROVIDER_BLURB, capabilityLines, fieldCosts, orderedProviders, providerView } from '../providers'
import { qk, useCreditsUsage, useMe, useSettings } from '../queries'
import { useApi } from '../auth'
import { usePageTitle } from '../usePageTitle'

function Tile({
  label,
  value,
  hint,
  tone,
}: Readonly<{
  label: string
  value: string
  hint?: string
  tone?: string
}>): JSX.Element {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h5" sx={{ color: tone, fontVariantNumeric: 'tabular-nums' }}>
          {value}
        </Typography>
        {hint ? (
          <Typography variant="caption" color="text.secondary">
            {hint}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  )
}

/**
 * Thirty days of spend as a bar per day. Deliberately not a charting library:
 * the only question it answers is "did something start burning credits", and a
 * row of divs answers it without adding a dependency to the bundle.
 */
function UsageBars({ days }: Readonly<{ days: Array<{ day: string; used: number }> }>): JSX.Element {
  const peak = days.reduce((max, d) => Math.max(max, d.used), 0)
  if (peak === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No credits spent in the last 30 days.
      </Typography>
    )
  }
  const peakDay = days.find((day) => day.used === peak)
  const total = days.reduce((sum, day) => sum + day.used, 0)
  // Every figure here is behind a hover, and a tooltip on a role-less div that
  // takes no focus can only be opened with a mouse — so thirty days of spend
  // were unreadable to a keyboard or a screen-reader admin. The row is labelled
  // as a picture, the way Breakdown is, and the same figures are stated in text
  // beside it.
  const summary = `Peak ${formatNumber(peak)} credits on ${formatDate(peakDay?.day)}, ${formatNumber(total)} across the last ${days.length} days.`
  return (
    <>
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="flex-end"
        sx={{ height: 88 }}
        role="img"
        aria-label={`Credits spent per day. ${summary}`}
      >
        {days.map((day) => (
          <Tooltip key={day.day} title={`${formatDate(day.day)}: ${formatNumber(day.used)} credits`}>
            <Box
              sx={{
                flex: 1,
                minWidth: 4,
                height: `${Math.max(2, (day.used / peak) * 100)}%`,
                bgcolor: day.used > 0 ? 'primary.main' : 'divider',
                borderRadius: 0.5,
              }}
            />
          </Tooltip>
        ))}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {summary}
      </Typography>
    </>
  )
}

/**
 * One provider, as a card that is also the radio for choosing it.
 *
 * `status` is null when the API did not report this provider at all. That is
 * shown as its own state rather than as an empty card: "not reported" and "no
 * key" are different problems with different fixes.
 */
/**
 * What is known about a provider that DID report: its balance, what it can do,
 * and what it cannot.
 *
 * Its own component so the card above holds the CHOICE — the name, the radio,
 * whether it is selected — and this holds the facts. A provider the API did not
 * report renders the other branch instead, deliberately: "not reported" and "no
 * key" are different problems with different fixes, and an empty card would say
 * neither.
 */
/**
 * The three things said about a provider beside its name.
 *
 * Whether it is in the race at all, whether it is the active one, and whether
 * its key has been seen to work — each with the reason it is worded the way it
 * is. Its own component because the wording is the substance: every one of
 * these labels was changed at least once for claiming more than the API knows.
 */
function ProviderBadges({
  label,
  status,
}: Readonly<{ label: string; status: ProviderStatus | null }>): JSX.Element {
  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      <Typography variant="subtitle1" fontWeight={600}>
        {label}
      </Typography>
      {/*
        A source that does not find people is not "Inactive" — it is not
        in this race at all, and labelling it as the loser of one it
        cannot enter reads as a source that is switched off.
      */}
      {status?.capabilities.discovery === false ? (
        <Tooltip title="Chosen per request on the Enrich dialog, rather than being the active provider for finding people.">
          <Chip size="small" variant="outlined" label="Enrichment only" />
        </Tooltip>
      ) : (
        <Chip
          size="small"
          label={status?.active ? 'Active' : 'Inactive'}
          color={status?.active ? 'success' : 'default'}
          variant={status?.active ? 'filled' : 'outlined'}
        />
      )}
      {status === null ? (
        <Chip size="small" variant="outlined" label="Not reported" />
      ) : (
        /* The API cannot read the key — that parameter is deliberately
           withheld from the internet-facing function — so it answers
           this from the last successful balance read. "Verified" is what
           that supports; "configured" would claim to have looked at a
           key nobody looked at, and would call a perfectly good key
           missing on the day it is first added. */
        <Tooltip
          title={
            status.keyConfigured
              ? 'A balance has been read from this provider, so its key works.'
              : 'No balance has ever been read from this provider, so its key has not been seen to work. Expected until a key is set; housekeeping polls every source that can be chosen.'
          }
        >
          <Chip
            size="small"
            variant="outlined"
            color={status.keyConfigured ? 'success' : 'warning'}
            label={status.keyConfigured ? 'Key verified' : 'Key unverified'}
          />
        </Tooltip>
      )}
    </Stack>
  )
}

function ProviderStatusDetail({
  label,
  status,
}: Readonly<{ label: string; status: ProviderStatus }>): JSX.Element {
  const lines = capabilityLines(status.capabilities)
  const missingFields = [
    ...(status.capabilities.personalEmail ? [] : ['personal email']),
    ...(status.capabilities.personalPhone ? [] : ['personal phone']),
  ]
  return (
    <Stack spacing={1.5}>
      <Box>
        <Typography variant="caption" color="text.secondary" display="block">
          Last known balance
        </Typography>
        <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {balanceText(status.capabilities.creditBalance, status.creditBalance)}
        </Typography>
        {/* Kept per provider. One shared number would read as zero for
            whichever provider was not polled last, and a zero balance is
            the one figure here that would stop someone starting a job. */}
        <Typography variant="caption" color="text.secondary">
          {status.creditBalanceAt
            ? `Read ${formatDateTime(status.creditBalanceAt)}. Held separately from the other provider's.`
            : 'Refreshed by the housekeeping schedule while this provider is active.'}
        </Typography>
      </Box>

      <Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
          What it can and cannot do
        </Typography>
        <Stack spacing={0.25}>
          {lines.map((line) => (
            <Stack key={line.key} direction="row" spacing={0.75} alignItems="flex-start">
              {line.supported ? (
                <CheckCircleOutlineIcon fontSize="small" color="success" />
              ) : (
                <RemoveCircleOutlineIcon fontSize="small" color={line.isLoss ? 'warning' : 'disabled'} />
              )}
              <Typography variant="body2" color={line.supported ? 'text.primary' : 'text.secondary'}>
                {line.text}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Box>

      {/* Said here, plainly, because this is the reason those buttons are
          greyed out on a screen two clicks away. */}
      {missingFields.length > 0 ? (
        <Alert severity="info" icon={false}>
          {label} has no endpoint for {missingFields.join(' or ')}, so{' '}
          {missingFields.length > 1 ? 'those options are' : 'that option is'} disabled on the Contacts screens
          rather than offered and then refused by the server.
        </Alert>
      ) : null}
    </Stack>
  )
}

function ProviderCard({
  id,
  label,
  status,
  selected,
  disabled,
  onSelect,
}: Readonly<{
  id: ProviderId
  label: string
  status: ProviderStatus | null
  selected: boolean
  disabled: boolean
  onSelect: () => void
}>): JSX.Element {
  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderColor: selected ? 'primary.main' : 'divider',
        borderWidth: selected ? 2 : 1,
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Radio
            checked={selected}
            disabled={disabled}
            onChange={onSelect}
            size="small"
            sx={{ mt: -0.5 }}
            inputProps={{ 'aria-label': `Use ${label}` }}
          />
          <Box sx={{ flexGrow: 1 }}>
            <ProviderBadges label={label} status={status} />

            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {PROVIDER_BLURB[id]}
            </Typography>
          </Box>
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        {status === null ? (
          <Typography variant="body2" color="text.secondary">
            This deployment&apos;s API did not report this provider, so nothing is known about its key, its
            balance or what it can do. Nothing is assumed here.
          </Typography>
        ) : (
          <ProviderStatusDetail label={label} status={status} />
        )}
      </CardContent>
    </Card>
  )
}

/**
 * The data provider choice.
 *
 * Above the credit controls because it governs them: every cap, every measured
 * cost and the balance itself belong to whichever provider is active.
 */
function DataProviderSection({ me }: Readonly<{ me: MeResponse | undefined }>): JSX.Element {
  /*
   * A refused switch and a broken one read differently.
   *
   * A running job blocking the change is expected and recoverable — the message
   * says to wait — so it is a warning with its own words rather than a raw
   * error panel, which would look like something went wrong.
   */
  const switchError = (): JSX.Element | null => {
    if (isConflict(switchProvider.error)) {
      return (
        <Alert severity="warning">
          <AlertTitle>The provider cannot change while a job is running</AlertTitle>
          {PROVIDER_SWITCH_CONFLICT}
        </Alert>
      )
    }
    if (switchProvider.isError) return <ErrorPanel error={switchProvider.error} />
    return null
  }

  const api = useApi()
  const queryClient = useQueryClient()
  const view = providerView(me)
  const [confirming, setConfirming] = useState<ProviderId | null>(null)

  const switchProvider = useMutation({
    mutationFn: (providerId: ProviderId) => api.setActiveProvider(providerId),
    onSuccess: async () => {
      setConfirming(null)
      // Settings and usage both read provider-scoped numbers, so they are as
      // stale as `me` is the moment the active provider changes.
      await queryClient.invalidateQueries({ queryKey: qk.me })
      await queryClient.invalidateQueries({ queryKey: qk.settings })
      await queryClient.invalidateQueries({ queryKey: qk.creditsUsage })
    },
  })

  const cards = orderedProviders(view)
  const target = cards.find((card) => card.id === confirming)
  const current = view.active
  // What the switch would cost in capability, named before it is made rather
  // than discovered later as a greyed-out button on another screen.
  const targetLosses = target?.status
    ? capabilityLines(target.status.capabilities).filter((line) => line.isLoss)
    : []

  function close(): void {
    switchProvider.reset()
    setConfirming(null)
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Data provider
        </Typography>
        <Typography variant="body2" color="text.secondary">
          One provider is active at a time and every new job uses it. The caps, the measured costs and the
          balance below all belong to whichever one is chosen here.
        </Typography>

        {!view.reported ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            <AlertTitle>Provider status is not reported by this API</AlertTitle>
            This deployment has not been upgraded to report which provider is active. Nothing is assumed from
            the browser, so the sources below stay unknown and none can be chosen until it is.
          </Alert>
        ) : null}

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {cards.map((card) => (
            <Grid key={card.id} size={{ xs: 12, md: 6 }}>
              <ProviderCard
                id={card.id}
                label={card.label}
                status={card.status}
                selected={view.activeId === card.id}
                // A provider the API did not report cannot be chosen either: the
                // confirmation below would have nothing true to say about its
                // key or what it cannot do, and this dialog exists to say both
                // before money moves.
                // A source that does not find people cannot be the active
                // provider either. Making one active would leave the product
                // with no ladder at all: every company failing on an endpoint
                // that does not exist, all the way to the dead-letter queue.
                // It is still shown, because it IS choosable — on the Enrich
                // dialog, where its role actually is.
                disabled={
                  !view.reported ||
                  card.status === null ||
                  card.status.capabilities.discovery === false ||
                  switchProvider.isPending ||
                  view.activeId === card.id
                }
                onSelect={() => setConfirming(card.id)}
              />
            </Grid>
          ))}
        </Grid>

        <Dialog open={target !== undefined} onClose={close} fullWidth maxWidth="sm">
          <DialogTitle>Switch to {target?.label}?</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2">
                Every job started from now on will use {target?.label}
                {current ? `, and ${current.label} will go inactive` : ''}. Jobs that have already run keep
                the provenance of the provider that found them.
              </Typography>
              <Typography variant="body2">
                Credit balances and measured costs are tracked separately per provider, so nothing recorded
                against {current?.label ?? 'the current provider'} is lost or merged, and the estimates in the
                job wizard will use {target?.label}&apos;s own measured costs once it has been calibrated.
              </Typography>
              {/* Stated as what is actually known. The API infers this from the
                  last successful balance read, and a provider that has never
                  been active has never been polled — so the first switch to a
                  brand-new provider always lands here, and telling that person
                  their jobs will fail would be wrong on the one occasion this
                  screen exists for. */}
              {target?.status && !target.status.keyConfigured ? (
                <Alert severity="warning">
                  <AlertTitle>Nothing has ever been read from {target.label}</AlertTitle>
                  Its key has not been seen to work, which is expected the first time a provider is made
                  active. If the key is not in Parameter Store, every job started after this switch will fail
                  — so check it before starting one.
                </Alert>
              ) : null}
              {targetLosses.length > 0 ? (
                <Alert severity="info">
                  <AlertTitle>What {target?.label} cannot do</AlertTitle>
                  <Stack component="ul" sx={{ m: 0, pl: 2.5 }} spacing={0.25}>
                    {targetLosses.map((line) => (
                      <Typography component="li" variant="body2" key={line.key}>
                        {line.text}
                      </Typography>
                    ))}
                  </Stack>
                </Alert>
              ) : null}

              {/* A running job is the expected reason this fails, and it is the
                  user's own to fix, so it gets a sentence rather than the red
                  panel every unexpected failure gets. */}
              {switchError()}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={close}>Cancel</Button>
            <Button
              variant="contained"
              disabled={switchProvider.isPending || target === undefined}
              onClick={() => {
                if (target) switchProvider.mutate(target.id)
              }}
            >
              {switchProvider.isPending ? 'Switching' : `Use ${target?.label}`}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  )
}

/**
 * Bounds mirror AppSettingsSchema and JobSettingsSchema so the form cannot
 * produce a body zod rejects.
 *
 * The one deliberate difference is the monthly cap's floor of 1 against the
 * schema's 0. `Number('')` is 0, so selecting that field to retype it and
 * saving stored a cap of zero, which the API accepts and then refuses every
 * Enrich request against until somebody thinks to re-read this screen.
 */
function clampInt(raw: string, min: number, max: number, fallback: number): number {
  const parsed = Number.parseInt(raw, 10)
  if (Number.isNaN(parsed)) return fallback
  return Math.min(max, Math.max(min, parsed))
}

interface Draft {
  killSwitch: boolean
  rateLimitPerMinute: number
  monthlyCreditCap: number
  alertEmail: string
  presetId: string
  creditCapPerCompany: number
  creditCapTotal: number
  minPeople: number
  includeCompanyProfile: boolean
  includeDirector: boolean
  autoWorkEmail: boolean
  enableFetch: boolean
}

function draftFrom(settings: AppSettings): Draft {
  const job: Partial<JobSettings> = settings.defaultJobSettings ?? {}
  return {
    killSwitch: settings.killSwitch,
    rateLimitPerMinute: settings.rateLimitPerMinute,
    monthlyCreditCap: settings.monthlyCreditCap,
    alertEmail: settings.alertEmail ?? '',
    presetId: job.presetId ?? 'founders_and_tech',
    creditCapPerCompany: job.creditCapPerCompany ?? 25,
    creditCapTotal: job.creditCapTotal ?? DEFAULT_CREDIT_CAP_TOTAL,
    minPeople: job.minPeople ?? 1,
    includeCompanyProfile: job.includeCompanyProfile ?? true,
    includeDirector: job.includeDirector ?? true,
    autoWorkEmail: job.autoWorkEmail ?? false,
    enableFetch: job.enableFetch ?? true,
  }
}

function patchFrom(draft: Draft): AppSettingsPatch {
  const preset = PRESETS[draft.presetId]
  return {
    killSwitch: draft.killSwitch,
    rateLimitPerMinute: draft.rateLimitPerMinute,
    monthlyCreditCap: draft.monthlyCreditCap,
    alertEmail: draft.alertEmail.trim() || null,
    defaultJobSettings: {
      presetId: draft.presetId,
      // Personas travel with the preset id: sending an id without its groups
      // would leave every new job with an empty persona list and no search to run.
      ...(preset ? { personas: preset.personas } : {}),
      creditCapPerCompany: draft.creditCapPerCompany,
      creditCapTotal: draft.creditCapTotal,
      minPeople: draft.minPeople,
      includeCompanyProfile: draft.includeCompanyProfile,
      includeDirector: draft.includeDirector,
      autoWorkEmail: draft.autoWorkEmail,
      enableFetch: draft.enableFetch,
    },
  }
}

/**
 * Whether a miss costs anything, when the provider says.
 *
 * Empty when unknown: silence is better than guessing at somebody's bill.
 */
function missChargeNote(freeOnMiss: boolean | null): string {
  if (freeOnMiss === null) return ''
  return freeOnMiss ? ', charged only when one is found' : ', charged whether or not one is found'
}

/** Which month the usage card is showing, or why it is showing nothing. */
function usageCaption(monthKey: string | null, failed: boolean): string {
  if (monthKey) return `Month ${monthKey}`
  return failed ? 'Usage could not be read' : 'Reading usage'
}

/** " Last changed <when> by <who>." — or nothing, when it never has been. */
function lastChanged(updatedAt: string | null | undefined, updatedBy: string | null | undefined): string {
  if (!updatedAt) return ''
  const who = updatedBy ? ` by ${updatedBy}` : ''
  return ` Last changed ${formatDateTime(updatedAt)}${who}.`
}

/**
 * The credit balance, or why there isn't one.
 *
 * Two different absences: a provider that does not report a balance at all, and
 * one that does but has not been polled yet. They are not the same thing to
 * somebody deciding whether it is safe to start a job.
 */
function balanceText(reportsBalance: boolean, balance: number | null): string {
  if (!reportsBalance) return 'Not reported by this provider'
  return balance === null ? 'Not read yet' : formatNumber(balance)
}

export function SettingsPage(): JSX.Element {
  usePageTitle('Settings')

  const api = useApi()
  const queryClient = useQueryClient()
  const me = useMe()
  const isAdmin = me.data?.isAdmin === true

  const settings = useSettings(me.isSuccess)
  const usage = useCreditsUsage(me.isSuccess)

  /**
   * The usage card's body: loading, then error, then data, then nothing.
   *
   * Sequential returns rather than a three-deep ternary in the JSX (Sonar
   * S3358). Four outcomes read as four lines here and as one nested expression
   * there, and the last two are genuinely different — `usage.data` absent after
   * a successful fetch is not the same as still fetching.
   */
  /*
   * The month's spend bar: a real figure when we have one, an indeterminate bar
   * while it is loading, and nothing at all when the read failed — a bar stuck
   * at zero would read as "no spend" rather than "unknown".
   */
  const usageBar = (): JSX.Element | null => {
    if (usageKnown) {
      return (
        <LinearProgress
          variant="determinate"
          value={percent(monthUsed, monthCap)}
          color={monthUsed > monthCap * 0.8 ? 'warning' : 'primary'}
          sx={{ height: 8, borderRadius: 4, mb: 2 }}
        />
      )
    }
    if (usage.isError) return null
    return <LinearProgress sx={{ height: 8, borderRadius: 4, mb: 2 }} />
  }

  const usagePanel = (): JSX.Element => {
    if (usage.isLoading) return <LoadingPanel label="Loading usage" />
    if (usage.isError) return <ErrorPanel error={usage.error} onRetry={() => void usage.refetch()} />
    if (usage.data) return <UsageBars days={usage.data.days} />
    return (
      <Typography variant="body2" color="text.secondary">
        Usage is unavailable.
      </Typography>
    )
  }

  const [draft, setDraft] = useState<Draft | null>(null)

  // Seeded once the settings arrive, and re-seeded only when the stored record
  // actually changes — otherwise a background refetch would wipe a half-edited form.
  useEffect(() => {
    if (settings.data) setDraft(draftFrom(settings.data))
  }, [settings.data])

  const save = useMutation({
    mutationFn: (patch: AppSettingsPatch) => api.updateSettings(patch),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qk.settings })
      await queryClient.invalidateQueries({ queryKey: qk.creditsUsage })
      await queryClient.invalidateQueries({ queryKey: qk.me })
    },
  })

  if (me.isLoading) return <LoadingPanel label="Loading settings" />
  /*
   * "We could not find out who you are" and "you are not an administrator" are
   * different sentences and used to share a screen.
   *
   * With no error branch, any /me outage rendered the permission wall — telling
   * a real administrator they were not one, on the page holding the kill switch
   * and the monthly cap, with no error, no retry and nothing saying to try
   * again. An error that reads as a policy is the worst kind: nobody reports it,
   * they just believe it.
   */
  if (me.isError) return <ErrorPanel error={me.error} onRetry={() => void me.refetch()} />
  if (!isAdmin) {
    return (
      <Card>
        <EmptyState
          title="Administrators only"
          description="These controls govern spending for everyone, so they are restricted. Ask an administrator if you need a cap changed or the kill switch lifted."
        />
      </Card>
    )
  }

  if (settings.isError) return <ErrorPanel error={settings.error} onRetry={() => void settings.refetch()} />
  if (!settings.data || !draft) return <LoadingPanel label="Loading settings" />

  const stored = settings.data
  // A failed usage read is not a month with no spend. Defaulting to zero drew a
  // "0 / 50,000" tile and an empty progress bar on the screen whose whole job is
  // spending control, and silently disabled the 80% warning tone with it, so
  // "we could not ask" looked exactly like "you have spent nothing".
  const usageKnown = usage.data !== undefined
  const monthUsed = usage.data?.month.used ?? 0
  const monthCap = usage.data?.month.cap ?? stored.monthlyCreditCap

  // The balance and key tiles belong to whichever provider is active. Until the
  // API reports the providers, they fall back to the legacy fields — which name
  // Enrich Layer in their own field names, so labelling them that way reads a
  // value rather than assuming one.
  const view = providerView(me.data)
  const active = view.active
  const activeLabel = active?.label ?? 'Enrich Layer'
  const balance = active ? active.creditBalance : stored.creditBalance
  const balanceAt = active ? active.creditBalanceAt : stored.creditBalanceAt
  const keyConfigured = active ? active.keyConfigured : me.data?.enrichLayerConfigured === true
  // The figure and the billing rule both belong to the active provider. Enrich
  // Layer charges 3 for a work email whether or not it finds one; EnrichmentAPI's
  // Email Finder is 2 and charges only on a hit. Stating one provider's terms
  // above a switch that spends the other's is the same defect as pricing the
  // enrich dialog from a single table, and this switch turns the purchase on for
  // every accepted contact in every job.
  const workEmailCost = fieldCosts(view).work_email
  /*
   * The billing basis for THIS purchase, not for the discovery ladder.
   *
   * `freeOnMiss` is the ladder's flag — "try the free-on-miss rungs first" — and
   * it is true for all three sources. Reading it here told people Enrich Layer's
   * work email is "charged only when one is found", which is the opposite of the
   * truth: three credits go on every call, hit or miss, and this switch turns
   * that on for every accepted contact in every job.
   */
  const workEmailFreeOnMiss = view.capabilities?.workEmailFreeOnMiss ?? null
  const workEmailWarning = [
    workEmailCost === null
      ? `${activeLabel} publishes no price for a work email`
      : `A work email costs ${formatNumber(workEmailCost)} credits from ${activeLabel}`,
    missChargeNote(workEmailFreeOnMiss),
    '. On by default it multiplies the cost of every job.',
  ].join('')

  const measured = Object.entries(stored.measuredCosts ?? {})
  const yieldDist = Object.entries(stored.yieldDistribution ?? {}).sort((a, b) => Number(a[0]) - Number(b[0]))

  function update<K extends keyof Draft>(key: K, value: Draft[K]): void {
    setDraft((current) => (current ? { ...current, [key]: value } : current))
  }

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h5">Settings</Typography>
        <Typography variant="body2" color="text.secondary">
          Spending controls and the defaults every new job starts from.
          {lastChanged(stored.updatedAt, stored.updatedBy)}
        </Typography>
      </Box>

      {stored.killSwitch ? (
        <Alert severity="error">
          <AlertTitle>The kill switch is on</AlertTitle>
          Every worker refuses to call the active data provider. Running jobs will stall rather than fail, and
          resume when it is switched off.
        </Alert>
      ) : null}

      <DataProviderSection me={me.data} />
      <ZohoCrmSection />

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Tile
            label={`${activeLabel} balance`}
            value={balance === null ? 'Unavailable' : formatNumber(balance)}
            hint={balanceAt ? `Read ${formatDateTime(balanceAt)}` : 'Refreshed by the housekeeping schedule'}
            tone={balance !== null && balance < 500 ? 'warning.main' : undefined}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Tile
            label="Used this month"
            value={usageKnown ? `${formatNumber(monthUsed)} / ${formatNumber(monthCap)}` : EM_DASH}
            hint={usageCaption(usage.data?.month.key ?? null, usage.isError)}
            tone={usageKnown && monthUsed > monthCap * 0.8 ? 'warning.main' : undefined}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Tile
            label={`${activeLabel} key`}
            value={keyConfigured ? 'Verified' : 'Not verified'}
            hint="Held as a SecureString in Parameter Store and never sent to the browser, so it cannot be shown even masked. Verified means a balance has been read with it."
            tone={keyConfigured ? 'success.main' : 'warning.main'}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Tile
            label="Microsoft sign-in"
            value={me.data?.ssoConfigured ? 'Microsoft Entra' : 'Cognito users only'}
            tone={me.data?.ssoConfigured ? 'success.main' : 'warning.main'}
          />
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Credit usage
          </Typography>
          <Typography variant="subtitle2" gutterBottom>
            Monthly cap
          </Typography>
          {usageBar()}
          <Typography variant="subtitle2" gutterBottom>
            Credits per day, last 30 days
          </Typography>
          {usagePanel()}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Spending controls
          </Typography>
          <Stack spacing={2} sx={{ maxWidth: 560 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={draft.killSwitch}
                  onChange={(event) => update('killSwitch', event.target.checked)}
                />
              }
              label="Kill switch — stop every outbound call to the data provider"
            />
            <TextField
              label="Monthly credit cap"
              type="number"
              value={draft.monthlyCreditCap}
              slotProps={{ htmlInput: { min: 1, max: 10_000_000 } }}
              onChange={(event) =>
                update(
                  'monthlyCreditCap',
                  clampInt(event.target.value, 1, 10_000_000, draft.monthlyCreditCap),
                )
              }
              helperText="Reserved before a spend, not counted after it, so a job stops at the cap rather than past it."
            />
            <TextField
              label="Requests per minute"
              type="number"
              value={draft.rateLimitPerMinute}
              slotProps={{ htmlInput: { min: 1, max: 300 } }}
              onChange={(event) =>
                update('rateLimitPerMinute', clampInt(event.target.value, 1, 300, draft.rateLimitPerMinute))
              }
              helperText="Enrich Layer allows 300 a minute, but a trial account before its first top-up is capped at 2."
            />
            <TextField
              label="Alert email"
              value={draft.alertEmail}
              onChange={(event) => update('alertEmail', event.target.value)}
              placeholder="marketing-ops@habilelabs.io"
            />
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Defaults for new jobs
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Every new job starts from these and can override them in the wizard.
          </Typography>
          <Stack spacing={2} sx={{ maxWidth: 560, mt: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="preset-label">Default persona preset</InputLabel>
              <Select
                labelId="preset-label"
                label="Default persona preset"
                value={draft.presetId}
                onChange={(event) => update('presetId', event.target.value)}
              >
                {Object.entries(PRESETS).map(([id, preset]) => (
                  <MenuItem key={id} value={id}>
                    {preset.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* The chosen preset's groups are shown rather than left implicit:
                the title query and page size are what a job actually spends on. */}
            <Stack spacing={1}>
              {(PRESETS[draft.presetId]?.personas ?? []).map((persona) => (
                <Box key={persona.key} sx={{ pl: 1, borderLeft: 2, borderColor: 'divider' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle2">{persona.label}</Typography>
                    <Chip size="small" variant="outlined" label={`page size ${persona.pageSize}`} />
                  </Stack>
                  <Typography variant="caption" color="text.secondary" className="mono">
                    {persona.titleQuery}
                  </Typography>
                </Box>
              ))}
            </Stack>

            <Divider />

            <TextField
              label="Credit cap per company"
              type="number"
              value={draft.creditCapPerCompany}
              slotProps={{ htmlInput: { min: 1, max: 200 } }}
              onChange={(event) =>
                update('creditCapPerCompany', clampInt(event.target.value, 1, 200, draft.creditCapPerCompany))
              }
              helperText="A hard ceiling across every rung of the ladder for one company."
            />
            <TextField
              label="Credit cap per job"
              type="number"
              value={draft.creditCapTotal}
              slotProps={{ htmlInput: { min: 1, max: 1_000_000 } }}
              onChange={(event) =>
                update('creditCapTotal', clampInt(event.target.value, 1, 1_000_000, draft.creditCapTotal))
              }
            />
            <TextField
              label="People per company"
              type="number"
              value={draft.minPeople}
              slotProps={{ htmlInput: { min: 1, max: 10 } }}
              onChange={(event) => update('minPeople', clampInt(event.target.value, 1, 10, draft.minPeople))}
              helperText="Real yield on the sample was 1.27 per company and never above 3."
            />

            <FormControlLabel
              control={
                <Switch
                  checked={draft.includeCompanyProfile}
                  onChange={(event) => update('includeCompanyProfile', event.target.checked)}
                />
              }
              label="Include the company profile lookup"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={draft.includeDirector}
                  onChange={(event) => update('includeDirector', event.target.checked)}
                />
              }
              label='Accept "Director" titles as decision-makers'
            />
            <FormControlLabel
              control={
                <Switch
                  checked={draft.autoWorkEmail}
                  onChange={(event) => update('autoWorkEmail', event.target.checked)}
                />
              }
              label="Buy a work email automatically for every accepted contact"
            />
            {draft.autoWorkEmail ? <Alert severity="warning">{workEmailWarning}</Alert> : null}
            <FormControlLabel
              control={
                <Switch
                  checked={draft.enableFetch}
                  onChange={(event) => update('enableFetch', event.target.checked)}
                />
              }
              label="Fetch company websites for domain triage and LinkedIn harvesting"
            />
            {!draft.enableFetch ? (
              <Alert severity="info">
                Both zero-credit rungs are off. On the real sample these are what rescued the companies whose
                websites had moved or expired.
              </Alert>
            ) : null}
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Calibration
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            The estimator reads measured per-endpoint costs, never constants. These are written by the
            calibration run and are read-only here.
            {/* Only stated when the record says so. An endpoint name means a
                different thing on each provider, and a table labelled with the
                wrong provider is worse than one labelled with none. */}
            {stored.measuredCostsProvider
              ? ` Measured against ${PROVIDER_LABEL[stored.measuredCostsProvider]}; each provider is calibrated on its own.`
              : ''}
          </Typography>

          {measured.length === 0 ? (
            <Alert severity="warning" sx={{ mt: 1 }}>
              <AlertTitle>No measured costs yet</AlertTitle>
              Estimates fall back to the plan&apos;s seed figures until calibration has run against the real
              account.
            </Alert>
          ) : (
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Endpoint</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Base
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Per result
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {measured.map(([endpoint, cost]) => (
                  <TableRow key={endpoint}>
                    <TableCell className="mono">{endpoint}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatNumber(cost.base)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatNumber(cost.perResult)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Typography variant="subtitle2" sx={{ mt: 2 }}>
            Yield distribution
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            The probability a company yields this many contacts. Seeded from the real 15-company sample and
            updated after every job; this is what drives the P10 to P90 band in the wizard.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
            {yieldDist.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {EM_DASH}
              </Typography>
            ) : (
              yieldDist.map(([people, probability]) => (
                <Chip
                  key={people}
                  size="small"
                  variant="outlined"
                  label={`${people} → ${(probability * 100).toFixed(1)}%`}
                  sx={{ fontVariantNumeric: 'tabular-nums' }}
                />
              ))
            )}
          </Stack>
        </CardContent>
      </Card>

      {save.isError ? <ErrorPanel error={save.error} /> : null}
      {save.isSuccess ? <Alert severity="success">Settings saved.</Alert> : null}

      <Stack direction="row" spacing={1}>
        <Button variant="contained" disabled={save.isPending} onClick={() => save.mutate(patchFrom(draft))}>
          {save.isPending ? 'Saving' : 'Save settings'}
        </Button>
        <Button disabled={save.isPending} onClick={() => setDraft(draftFrom(stored))}>
          Discard changes
        </Button>
      </Stack>

      <Typography variant="caption" color="text.secondary">
        The {Object.keys(PRESETS).length} presets themselves are code constants shared with the workers, so a
        change to a title query ships with a deploy rather than from this screen.
      </Typography>
    </Stack>
  )
}
